import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useGallery } from '../context/GalleryContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useUploadQueue } from '../hooks/useUploadQueue';
import { getUploadFileType, UPLOAD_FILE_ACCEPT } from '../utils/mediaFileType';
import {
  MAX_PHOTO_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_BYTES,
  PHOTO_UPLOAD_LIMIT_LABEL,
  UPLOAD_LIMIT_SUMMARY,
  VIDEO_UPLOAD_LIMIT_LABEL,
} from '../config/uploadLimits';

const describeFileNames = (names) => names.length <= 3
  ? names.join(', ')
  : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;

const UploadForm = ({ onUploadComplete, onUploadTabActivate }) => {
  const [uploading, setUploading] = useState(false);
  const [preparedFiles, setPreparedFiles] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [rejections, setRejections] = useState([]);
  const [queueError, setQueueError] = useState('');
  const [queuedCount, setQueuedCount] = useState(0);
  const fileInputRef = useRef(null);
  const blobUrlsRef = useRef(new Set());
  const queuedPreviewsRef = useRef(new Map());
  const mountedRef = useRef(true);
  const savingRef = useRef(false);
  const { galleryId } = useGallery();
  const galleryIdRef = useRef(galleryId);
  galleryIdRef.current = galleryId;
  const { isOnline } = useNetworkStatus();
  const { enqueue, queue = [] } = useUploadQueue();
  const uploadPolicyMessage = queue.find(item => item.galleryId === galleryId && item.policyBlocked)?.error || '';

  const releasePreviews = useCallback(() => {
    blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    blobUrlsRef.current.clear();
    queuedPreviewsRef.current.clear();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releasePreviews();
    };
  }, [releasePreviews]);

  const previousGalleryIdRef = useRef(galleryId);
  useEffect(() => {
    if (previousGalleryIdRef.current !== galleryId) {
      releasePreviews();
      setPreparedFiles([]);
      setShowConfirmation(false);
      setRejections([]);
      setQueueError('');
      setQueuedCount(0);
      setUploading(false);
      previousGalleryIdRef.current = galleryId;
    }
  }, [galleryId, releasePreviews]);

  // Queue records are authoritative. Transient previews may be handed to the gallery only
  // after upload succeeds; navigating away simply releases them, never the queued file.
  useEffect(() => {
    const completed = queue.filter(item => item.galleryId === galleryId && item.status === 'done' && queuedPreviewsRef.current.has(item.id));
    if (!completed.length) return;
    const notifications = completed.map(item => {
      const preview = queuedPreviewsRef.current.get(item.id);
      queuedPreviewsRef.current.delete(item.id);
      if (onUploadComplete) blobUrlsRef.current.delete(preview.preview);
      return {
        ...preview,
        id: item.id,
        status: 'success',
        originalPath: item.originalPath || null,
        mediaId: item.mediaId || null,
        contentType: item.contentType,
      };
    });
    onUploadComplete?.(notifications);
  }, [galleryId, onUploadComplete, queue, queuedCount]);

  const onDrop = useCallback((selectedFiles) => {
    if (savingRef.current) return;
    const files = Array.from(selectedFiles || []);
    if (!files.length) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    const validFiles = [];
    const unsupported = [];
    const oversizedPhotos = [];
    const oversizedVideos = [];
    for (const file of files) {
      const { kind } = getUploadFileType(file);
      if (!kind) unsupported.push(file.name);
      else if (file.size > (kind === 'video' ? MAX_VIDEO_UPLOAD_BYTES : MAX_PHOTO_UPLOAD_BYTES)) {
        (kind === 'video' ? oversizedVideos : oversizedPhotos).push(file.name);
      } else validFiles.push(file);
    }
    setRejections([
      ...(unsupported.length ? [`File type not supported: ${describeFileNames(unsupported)}`] : []),
      ...(oversizedPhotos.length ? [`Photos exceed ${PHOTO_UPLOAD_LIMIT_LABEL}: ${describeFileNames(oversizedPhotos)}`] : []),
      ...(oversizedVideos.length ? [`Videos exceed ${VIDEO_UPLOAD_LIMIT_LABEL}: ${describeFileNames(oversizedVideos)}`] : []),
    ]);
    setQueueError('');
    setQueuedCount(0);
    // Release only the previous selection. Pending uploads retain their previews until
    // completion or unmount; their original files belong to the app-level queue.
    preparedFiles.forEach(item => {
      URL.revokeObjectURL(item.preview);
      blobUrlsRef.current.delete(item.preview);
    });
    const processedFiles = validFiles.map((file, index) => {
      const { kind: fileType, mimeType } = getUploadFileType(file);
      const preview = URL.createObjectURL(file.type === mimeType ? file : file.slice(0, file.size, mimeType));
      blobUrlsRef.current.add(preview);
      return { file, preview, fileType, name: file.name, id: `${Date.now()}-${index}` };
    });
    setPreparedFiles(processedFiles);
    setShowConfirmation(validFiles.length > 0);
  }, [preparedFiles]);

  const handleFileSelect = event => onDrop(event.target.files);
  const handleDragOver = event => { event.preventDefault(); event.stopPropagation(); };
  const handleDrop = event => {
    event.preventDefault(); event.stopPropagation(); onDrop(event.dataTransfer.files);
  };
  const removeFile = id => {
    const removed = preparedFiles.find(item => item.id === id);
    if (removed) {
      URL.revokeObjectURL(removed.preview);
      blobUrlsRef.current.delete(removed.preview);
    }
    const remaining = preparedFiles.filter(item => item.id !== id);
    setPreparedFiles(remaining);
    setShowConfirmation(remaining.length > 0);
  };
  const cancelUpload = () => {
    preparedFiles.forEach(item => {
      URL.revokeObjectURL(item.preview);
      blobUrlsRef.current.delete(item.preview);
    });
    setPreparedFiles([]);
    setShowConfirmation(false);
    setRejections([]);
    setQueueError('');
  };
  const confirmUpload = async () => {
    if (savingRef.current || !preparedFiles.length) return;
    savingRef.current = true;
    setUploading(true);
    setQueueError('');
    const operationGalleryId = galleryId;
    const selection = preparedFiles;
    try {
      // Always persist the entire selection before starting network work. This lives above
      // routes, so changing tabs does not cancel uploads or discard later batches.
      const items = await enqueue(selection.map(item => item.file), operationGalleryId);
      if (items.length !== selection.length) throw new Error('Unable to save uploads. Please try again.');
      if (!mountedRef.current || galleryIdRef.current !== operationGalleryId) return;
      items.forEach((item, index) => queuedPreviewsRef.current.set(item.id, selection[index]));
      setQueuedCount(items.length);
      setPreparedFiles([]);
      setShowConfirmation(false);
      setRejections([]);
    } catch (error) {
      if (mountedRef.current && galleryIdRef.current === operationGalleryId) {
        // Keep the selected files and confirmation so local storage failures are recoverable.
        setQueueError(error instanceof Error ? error.message : 'Unable to save uploads. Please try again.');
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current && galleryIdRef.current === operationGalleryId) setUploading(false);
    }
  };

  return (
    <div className="upload-container">
      {uploadPolicyMessage ? (
        <div className="mb-4 rounded-xl border border-accent bg-accent-20 p-4 text-secondary" role="alert">
          <p className="font-semibold">A previous upload could not be accepted</p>
          <p className="mt-1 text-sm leading-relaxed">{uploadPolicyMessage}</p>
          <p className="mt-1 text-xs text-secondary-70">If the owner updated gallery access or the upload allowance, choose your files again to check.</p>
        </div>
      ) : null}
      {rejections.length > 0 && (
        <div role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Some files were skipped</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">{rejections.map(message => <li key={message}>{message}</li>)}</ul>
          {preparedFiles.length > 0 && <p className="mt-2">Your other {preparedFiles.length} {preparedFiles.length === 1 ? 'file is' : 'files are'} ready to upload.</p>}
        </div>
      )}
      {queueError && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{queueError}</p>}
      {queuedCount > 0 && <div role="status" className="mb-4 rounded-xl border border-accent bg-accent-20 p-4 text-sm text-secondary">
        <p className="font-semibold">{queuedCount} {queuedCount === 1 ? 'file' : 'files'} added to uploads</p>
        <p className="mt-1">{isOnline ? 'You can browse other sections while uploads continue.' : 'Uploads will start when you reconnect.'} Keep this tab open to continue uploading; saved files resume when you return.</p>
        <p className="mt-1">Check progress or retry files in Upload status below.</p>
      </div>}
      {/* Drag and Drop Area - Only show when no files are prepared */}
      {!showConfirmation && (
        <div
          className={`border-2 border-dashed border-primary rounded-xl p-8 text-center bg-primary-10 transition-all duration-200 ${uploading ? 'opacity-50 pointer-events-none' : 'hover:bg-primary-20'}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={UPLOAD_FILE_ACCEPT}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />


          <div className="upload-content">
            <svg className="mx-auto h-12 w-12 text-primary mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>

            <p className="text-secondary font-medium text-lg mb-2">
              {uploading ? 'Saving uploads...' : 'Drop photos and videos here'}
            </p>

            <p className="text-secondary-70 text-sm mb-4">
              {uploading ? 'Saving your files to the upload queue...' : 'Capture or share your favourite moments!'}
            </p>

            {/* Single upload button that opens native picker */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  if (!uploading) {
                    if (onUploadTabActivate) {
                      onUploadTabActivate();
                    }
                    fileInputRef.current?.click();
                  }
                }}
                disabled={uploading}
                className="flex items-center justify-center gap-2 px-8 py-3 bg-primary text-surface rounded-lg hover:bg-primary-80 transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Choose Photos & Videos
              </button>
            </div>

            <p className="text-primary text-xs mt-4">
              {UPLOAD_LIMIT_SUMMARY}
            </p>
          </div>
        </div>
      )}

      {/* Prepared Files Grid - Show when files are ready for confirmation */}
      {showConfirmation && preparedFiles.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="grid grid-cols-1">
              <h3 className="font-bold text-primary text-lg">
                Ready<br />
              </h3>
              <p className="text-secondary-70 text-sm">
                ({preparedFiles.length} file{preparedFiles.length > 1 ? 's' : ''})
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={cancelUpload}
                disabled={uploading}
                className="px-4 py-2 text-secondary hover:text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="px-6 py-2 bg-primary text-surface rounded-lg hover:bg-primary-80 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Saving uploads...' : 'Upload'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1">
            {preparedFiles.map((file) => (
              <div key={file.id} className="relative group aspect-square overflow-hidden">
                {file.fileType === 'video' ? (
                  // Video preview
                  <div className="video-thumbnail relative w-full h-full">
                    <video
                      src={file.preview}
                      className="w-full h-full object-cover rounded shadow-md"
                      muted
                      preload="metadata"
                    />
                    {/* Video indicator */}
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-surface text-xs px-2 py-1 rounded">
                      <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                      </svg>
                      VIDEO
                    </div>
                    {/* Play button overlay
                    <div className="play-button">
                      <svg className="w-6 h-6 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div> */}
                  </div>
                ) : (
                  // Image preview
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="w-full h-full object-cover rounded shadow-md"
                  />
                )}

                {/* Remove button */}
                <button
                  onClick={() => removeFile(file.id)}
                  disabled={uploading}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-surface rounded-full w-8 h-8 m-auto text-lg opacity-70 hover:opacity-100 transition-all focus:outline-none"
                  style={{ pointerEvents: 'auto' }}
                  aria-label="Remove file"
                >
                  ×
                </button>

                {/* File info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 px-2 py-1 text-xs text-surface truncate">
                  {file.name}
                </div>

                {/* Thumbnail ready indicator */}
                {file.thumbnail && (
                  <div className="absolute top-2 right-2 bg-primary text-surface rounded px-2 py-1 text-xs shadow-md opacity-90">
                    Ready
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default UploadForm;
